import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, AlertCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';

const TYPE_COLORS = {
  INGREDIENT: '#16a34a',
  PACKAGING: '#2563eb',
  CONSUMABLE: '#d97706',
  EQUIPMENT: '#6366f1',
  CROCKERY_CUTLERY: '#0891b2',
  UNCLASSIFIED: '#94a3b8',
};

const formatCurrency = (v) => {
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toFixed(0);
};

const CostStructureSection = ({ refreshKey }) => {
  const { theme } = useTheme();
  const c = colors[theme];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [driversOpen, setDriversOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.getCostStructure();
      setData(res.data);
    } catch (e) {
      console.error('Error loading cost structure:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Silent refresh on refreshKey change
  useEffect(() => {
    if (refreshKey > 0) {
      api.getCostStructure()
        .then((res) => setData(res.data))
        .catch(() => {});
    }
  }, [refreshKey]);

  if (loading) {
    return (
      <div
        className="rounded-xl p-6 mb-4"
        style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}
        data-testid="cost-structure-loading"
      >
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={16} style={{ color: c.textSecondary }} />
          <span className="text-sm font-semibold" style={{ color: c.textPrimary }}>Cost Structure Breakdown</span>
        </div>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 rounded-lg" style={{ background: c.background }} />
          ))}
        </div>
      </div>
    );
  }

  if (!data || !data.breakdown) return null;

  const { breakdown, total_spend, nudge, top_unclassified = [] } = data;
  const nonZero = breakdown.filter((b) => b.total_spend > 0);
  const maxPct = Math.max(...breakdown.map((b) => b.pct_of_total), 1);

  return (
    <motion.div
      className="rounded-xl p-5 mb-4"
      style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="cost-structure-section"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 size={16} style={{ color: c.accentBlue }} />
          <span className="text-sm font-semibold" style={{ color: c.textPrimary }}>
            Cost Structure Breakdown
          </span>
        </div>
        <span className="text-xs font-mono" style={{ color: c.textSecondary }}>
          Total: {formatCurrency(total_spend)}
        </span>
      </div>

      {/* Stacked horizontal bar */}
      <div className="flex rounded-lg overflow-hidden h-6 mb-4" style={{ background: c.background }} data-testid="cost-structure-bar">
        {nonZero.map((b) => (
          <div
            key={b.item_type}
            className="h-full transition-all relative group"
            style={{
              width: `${b.pct_of_total}%`,
              background: TYPE_COLORS[b.item_type] || '#94a3b8',
              minWidth: b.pct_of_total > 0 ? '2px' : '0',
            }}
            title={`${b.label}: ${b.pct_of_total}%`}
            data-testid={`bar-segment-${b.item_type}`}
          >
            {b.pct_of_total > 8 && (
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white">
                {b.pct_of_total}%
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Breakdown rows */}
      <div className="space-y-2" data-testid="cost-structure-rows">
        {breakdown.map((b) => {
          const barWidth = maxPct > 0 ? (b.pct_of_total / maxPct) * 100 : 0;
          const typeColor = TYPE_COLORS[b.item_type] || '#94a3b8';

          return (
            <div key={b.item_type} className="flex items-center gap-3" data-testid={`cost-row-${b.item_type}`}>
              {/* Color dot + label */}
              <div className="flex items-center gap-2 w-44 shrink-0">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: typeColor }} />
                <span
                  className={`text-xs font-medium truncate ${b.total_spend === 0 ? 'opacity-40' : ''}`}
                  style={{ color: c.textPrimary }}
                >
                  {b.label}
                </span>
              </div>

              {/* Bar */}
              <div className="flex-1 h-5 rounded relative" style={{ background: c.background }}>
                {b.pct_of_total > 0 && (
                  <motion.div
                    className="h-full rounded"
                    style={{ background: typeColor + '30', width: `${barWidth}%` }}
                    initial={{ width: 0 }}
                    animate={{ width: `${barWidth}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  />
                )}
              </div>

              {/* Values */}
              <div className="flex items-center gap-3 shrink-0 w-36 justify-end">
                <span className="text-xs font-mono tabular-nums" style={{ color: c.textSecondary }}>
                  {b.line_item_count > 0 ? `${b.line_item_count} items` : '-'}
                </span>
                <span
                  className="text-xs font-semibold tabular-nums w-14 text-right"
                  style={{ color: b.total_spend > 0 ? c.textPrimary : c.textSecondary }}
                >
                  {b.pct_of_total > 0 ? `${b.pct_of_total}%` : '-'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Nudge */}
      {nudge && (
        <div
          className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
          style={{ background: c.accentYellow + '12', color: c.accentYellow, border: `1px solid ${c.accentYellow}25` }}
          data-testid="cost-structure-nudge"
        >
          <Info size={13} />
          {nudge}
        </div>
      )}

      {/* Top Unclassified Spend Drivers */}
      {top_unclassified.length > 0 && (
        <div className="mt-3" data-testid="top-unclassified-section">
          <button
            onClick={() => setDriversOpen(!driversOpen)}
            className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors"
            style={{ background: c.background, color: c.textPrimary }}
            data-testid="top-unclassified-toggle"
          >
            {driversOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            Top Unclassified Spend Drivers
            <span className="ml-auto font-normal" style={{ color: c.textSecondary }}>
              {top_unclassified.length} items
            </span>
          </button>
          <AnimatePresence>
            {driversOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 space-y-1" data-testid="top-unclassified-list">
                  {top_unclassified.map((item, i) => (
                    <div
                      key={`${item.item_name}-${item.vendor_name}`}
                      className="flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs"
                      style={{ background: i % 2 === 0 ? 'transparent' : c.background + '60' }}
                      data-testid={`unclassified-row-${i}`}
                    >
                      <span className="w-5 text-right font-mono shrink-0" style={{ color: c.textSecondary }}>
                        {i + 1}
                      </span>
                      <span className="flex-1 font-medium truncate" style={{ color: c.textPrimary }}>
                        {item.item_name}
                      </span>
                      <span className="w-36 truncate shrink-0" style={{ color: c.textSecondary }}>
                        {item.vendor_name}
                      </span>
                      <span className="w-12 text-right font-mono shrink-0" style={{ color: c.textSecondary }}>
                        {item.line_count}x
                      </span>
                      <span className="w-20 text-right font-semibold font-mono shrink-0" style={{ color: c.textPrimary }}>
                        {formatCurrency(item.total_spend)}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default CostStructureSection;
